'use client';

import {
  Activity,
  Flash,
  InfoIcon2,
  Scale,
  Server,
  TagUser,
} from '@/components/icons/Icons';
import { IconContainer } from '@/components/icons/IconsContainer';
import { reactMarkdownConfig } from '@/lib/utils/strapi';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';

type TextBlock = {
  title: string;
  text: string;
};

type MethodStep = TextBlock & {
  number: string;
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const offerIcons = [InfoIcon2, Flash, Activity, Server, Scale, TagUser];

export function WhyNowSection({ title, intro, blocks }: { title: string; intro: string; blocks: TextBlock[] }) {
  return (
    <div className="section bg-content1 text-current">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader title={title} intro={intro} />
        <div className="section-body grid gap-4 md:grid-cols-3">
          {blocks.map((block) => (
            <motion.article
              key={block.title}
              className="ai-panel"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <h3 className="ai-panel-title">{block.title}</h3>
              <ReactMarkdown className="section-prose" {...reactMarkdownConfig}>
                {block.text}
              </ReactMarkdown>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}

export function OffersSection({ title, intro, offers }: { title: string; intro: string; offers: TextBlock[] }) {
  return (
    <div className="section bg-gradient-to-t from-content2 to-content1 text-current">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader title={title} intro={intro} />
        <div className="section-body grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {offers.map((offer, index) => {
            const Icon = offerIcons[index % offerIcons.length];
            return (
              <motion.article
                key={offer.title}
                className="ai-panel ai-panel--offer"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <div className="ai-icon-wrap">
                  <IconContainer Icon={Icon} iconProps={{ className: 'w-7 h-7 text-primary fill-current' }} />
                </div>
                <h3 className="ai-panel-title">{offer.title}</h3>
                <ReactMarkdown className="section-prose" {...reactMarkdownConfig}>
                  {offer.text}
                </ReactMarkdown>
              </motion.article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function MethodSection({ title, intro, steps }: { title: string; intro: string; steps: MethodStep[] }) {
  return (
    <div className="section bg-content1 text-current">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader title={title} intro={intro} />
        <div className="section-body grid gap-3 lg:grid-cols-5">
          {steps.map((step) => (
            <motion.article
              key={step.number}
              className="ai-method-step"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <span className="ai-step-number">{step.number}</span>
              <h3 className="ai-panel-title">{step.title}</h3>
              <ReactMarkdown className="section-prose" {...reactMarkdownConfig}>
                {step.text}
              </ReactMarkdown>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}

export function UseCasesSection({ title, intro, list }: { title: string; intro: string; list: string }) {
  return (
    <div className="section bg-gradient-to-t from-content1 to-content2 text-current">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader title={title} intro={intro} />
        <motion.div
          className="section-body ai-use-cases"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <ReactMarkdown className="section-prose" {...reactMarkdownConfig}>
            {list}
          </ReactMarkdown>
        </motion.div>
      </div>
    </div>
  );
}

export function TrustSection({ title, text }: { title: string; text: string }) {
  return (
    <div className="section bg-content1 text-current">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          className="ai-trust-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <div>
            <p className="ai-section-kicker">Software delivery first</p>
            <h2 className="section-title text-left mb-6">{title}</h2>
          </div>
          <ReactMarkdown className="section-prose" {...reactMarkdownConfig}>
            {text}
          </ReactMarkdown>
        </motion.div>
      </div>
    </div>
  );
}

function SectionHeader({ title, intro }: { title: string; intro: string }) {
  return (
    <motion.div
      className="mx-auto max-w-3xl text-center"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUp}
    >
      <h2 className="section-title">{title}</h2>
      <ReactMarkdown className="section-prose ai-section-intro" {...reactMarkdownConfig}>
        {intro}
      </ReactMarkdown>
    </motion.div>
  );
}
